/**
 * @file CarouselV2 component
 *
 * A simple carousel component based on Swiper.
 */

import SelectorEngine from '../../utils/dom/selector-engine';
import Manipulator from '../../utils/dom/manipulator';
import EventHandler from '../../utils/dom/event-handler';
import BaseComponent from '../BaseComponent';

const NAME = 'CarouselV2';

const DATA_KEY = 'lta.carouselv2';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';

const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
const SELECTOR_DATA_TOGGLE = '[data-lta-carouselv2]';

const HIDDEN_CLASS = 'u-hidden';
const VISIBLE = 'visible';
const HIDDEN = 'hidden';

class CarouselV2 extends BaseComponent {
	// eslint-disable-next-line unicorn/no-null
	constructor(element = null, settings = {}) {
		super(element);

		const defaults = {
			slidesPerView: 1,
			slidesPerViewMd: 2,
			slidesPerViewXl: 4,
			spaceBetween: 16,
			spaceBetweenMd: 16,
			spaceBetweenXl: 24
		};

		this.settings = {
			...defaults,
			...settings
		};

		this.componentWrapper_ = element;
		this.swiperInstance = undefined;
		this.Swiper = undefined;
		this.Accessibility = undefined;
		this.KeyboardControl = undefined;
		this.Navigation = undefined;
		this.Pagination = undefined;

		if (this.componentWrapper_) {
			const instanceSettings = Manipulator.getDataAttributes(element);
			const hideCarouselControls = instanceSettings.carouselHideControls || false;
			const previousButton = SelectorEngine.findOne('.lta-carouselv2__prev', element);
			const nextButton = SelectorEngine.findOne('.lta-carouselv2__next', element);
			const isMobile = window.innerWidth < 768;

			if (hideCarouselControls) {
				if (isMobile) {
					previousButton.classList.add(HIDDEN_CLASS);
				} else {
					previousButton.style.visibility = HIDDEN;
				}
			}

			import('swiper')
				.then(({ Swiper, Navigation, Pagination, A11y, Keyboard }) => {
					this.Swiper = Swiper;
					this.Accessibility = A11y;
					this.KeyboardControl = Keyboard;
					this.Navigation = Navigation;
					this.Pagination = Pagination;

					this.initCarouselV2Settings(instanceSettings);
					this.initCarouselV2(
						SelectorEngine.findOne('.swiper', this.componentWrapper_),
						hideCarouselControls,
						previousButton,
						nextButton
					);

					return Swiper;
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error(error);
				});
		}
	}

	static get NAME() {
		return NAME;
	}

	initCarouselV2Settings(instanceSettings) {
		if (instanceSettings.slidesPerView !== undefined) this.settings.slidesPerView = instanceSettings.slidesPerView;
		if (instanceSettings.slidesPerViewMd !== undefined) this.settings.slidesPerViewMd = instanceSettings.slidesPerViewMd;
		if (instanceSettings.slidesPerViewXl !== undefined) this.settings.slidesPerViewXl = instanceSettings.slidesPerViewXl;

		if (instanceSettings.spaceBetween !== undefined) this.settings.spaceBetween = instanceSettings.spaceBetween;
		if (instanceSettings.spaceBetweenMd !== undefined) this.settings.spaceBetweenMd = instanceSettings.spaceBetweenMd;
		if (instanceSettings.spaceBetweenXl !== undefined) this.settings.spaceBetweenXl = instanceSettings.spaceBetweenXl;
	}

	initCarouselV2(swiperElement, hideCarouselControls, previousButton, nextButton) {
		const count = swiperElement.querySelector('.swiper-wrapper').children.length;
		this.swiperInstance = new this.Swiper(swiperElement, {
			modules: [this.Navigation, this.Pagination, this.Accessibility, this.KeyboardControl],
			slidesPerView: this.settings.slidesPerView,
			spaceBetween: this.settings.spaceBetween,
			breakpoints: {
				768: {
					slidesPerView: this.settings.slidesPerViewMd,
					spaceBetween: this.settings.spaceBetweenMd,
				},
				1200: {
					slidesPerView: this.settings.slidesPerViewXl,
					spaceBetween: this.settings.spaceBetweenXl,
				}
			},
			navigation: {
				prevEl: '.lta-carouselv2__prev',
				nextEl: '.lta-carouselv2__next'
			},
			watchSlidesProgress: true,
			grabCursor: true,
			observer: true,
			a11y: {
				enabled: true
			},
			keyboard: {
				enabled: true,
				onlyInViewport: false
			},
			pagination: {
				el: '.lta-carouselv2__pagination',
				clickable: true,
				renderBullet(index, className) {
					return `<button class="${className} ${className}${index}" aria-label="Slide ${index+1} of ${count}"></button>`;
				}
			},
			on: {
				slideChange: () => {
					// To get the active index of swiper
					const activeIndex = this.swiperInstance.activeIndex;

					// To get the current screen size
					const isDesktop = window.innerWidth >= 1200;
					const isTablet = window.innerWidth >= 768 && window.innerWidth < 1200;
					const isMobile = window.innerWidth < 768;

					if (hideCarouselControls) {
						// Hide or show previousButton and nextButton based on the active index
						if (activeIndex === 0) {
							if (isMobile) {
								previousButton.classList.add(HIDDEN_CLASS);
								nextButton.classList.remove(HIDDEN_CLASS);
							} else {
								previousButton.style.visibility = HIDDEN;
								nextButton.style.visibility = VISIBLE;
							}
						} else if (isDesktop && activeIndex === count - this.settings.slidesPerViewXl) {
							previousButton.style.visibility = VISIBLE;
							nextButton.style.visibility = HIDDEN;
							// Disable next button on desktop
							nextButton.disabled = true;
						} else if (isTablet && activeIndex === count - this.settings.slidesPerViewMd) {
							previousButton.style.visibility = VISIBLE;
							nextButton.style.visibility = HIDDEN;
							// Disable next button on tablet
							nextButton.disabled = true;
						} else if (isMobile && activeIndex === count - this.settings.slidesPerView) {
							previousButton.classList.remove(HIDDEN_CLASS);
							nextButton.classList.add(HIDDEN_CLASS);
							// Disable next button on mobile
							nextButton.disabled = true;
						} else {
							// Show both buttons for other indices and enable next button
							previousButton.classList.remove(HIDDEN_CLASS);
							nextButton.classList.remove(HIDDEN_CLASS);
							nextButton.disabled = false;
						}
					}
				},
			},
		});
	}
}

/**
 * Data Api implementation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
	event.preventDefault();

	CarouselV2.getInstance(this) || new CarouselV2(this);
});

export default CarouselV2;
